import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Tag, Wand2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { AVAILABLE_ICONS, getCategoryIcon } from "@/constants/categoryIcons";
import { Category } from "@/types/database";
import { EmptyState } from "../ui/EmptyState";

const normalizeName = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

// Mapeamento de palavras-chave para ícones
const ICON_KEYWORDS: Record<string, string[]> = {
  "food": ["alimentacao", "comida", "restaurante", "almoco", "jantar", "lanche", "ifood", "delivery"],
  "shopping": ["mercado", "supermercado", "compras", "shopping", "loja"],
  "car": ["transporte", "carro", "uber", "taxi", "99", "estacionamento", "veiculo", "ipva"],
  "fuel": ["gasolina", "combustivel", "abastecimento", "posto"],
  "home": ["casa", "moradia", "aluguel", "condominio", "habitacao", "apartamento"],
  "utilities": ["luz", "agua", "energia", "gas", "conta"],
  "internet": ["internet", "wifi", "banda larga"],
  "health": ["saude", "farmacia", "remedio", "hospital", "clinica", "dentista"],
  "fitness": ["academia", "esporte", "fit", "gym"],
  "education": ["educacao", "escola", "faculdade", "curso", "leitura"],
  "entertainment": ["lazer", "diversao", "cinema", "filme", "serie", "streaming", "netflix", "spotify"],
  "salary": ["salario", "pagamento", "renda", "proventos"],
  "investment": ["investimento", "aplicacao", "cdb", "acoes", "fii"],
  "savings": ["poupanca", "reserva", "guardar"],
  "card": ["cartao", "fatura", "credito"],
  "gift": ["presente", "doacao"],
  "travel": ["viagem", "ferias", "passagem", "hospedagem"],
  "pets": ["pet", "cachorro", "gato", "veterinario", "racao"],
  "services": ["servico", "manutencao", "limpeza", "reparo"],
};

interface CategoryManagerProps {
  onCategoryUpdate?: () => void;
}

const colors = [
  { value: "#3B82F6", label: "Azul" },
  { value: "#10B981", label: "Verde" },
  { value: "#F59E0B", label: "Amarelo" },
  { value: "#EF4444", label: "Vermelho" },
  { value: "#8B5CF6", label: "Roxo" },
  { value: "#06B6D4", label: "Ciano" },
  { value: "#F97316", label: "Laranja" },
  { value: "#84CC16", label: "Lima" },
  { value: "#EC4899", label: "Rosa" },
  { value: "#6B7280", label: "Cinza" },
];

export function CategoryManager({ onCategoryUpdate }: CategoryManagerProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    nome: "",
    tipo: "ambos" as "entrada" | "saida" | "ambos",
    cor: "#3B82F6",
    icone: "default",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useAuth();


  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categorias")
        .select("*")
        .order("nome");

      if (error) throw error;
      setCategories((data as Category[]) || []);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      toast.error("Erro ao carregar categorias");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      toast.error("Nome da categoria é obrigatório");
      return;
    }

    setLoading(true);

    try {
      const normalizedName = formData.nome.trim();

      if (editingCategory) {

        const { error } = await supabase
          .from("categorias")
          .update({
            nome: normalizedName,
            tipo: formData.tipo,
            cor: formData.cor,
            icone: formData.icone,
          })
          .eq("id", editingCategory.id);

        if (error) {
          const e = error as unknown;
          const isUnique = typeof e === "object" && e !== null && "code" in (e as Record<string, unknown>) && (e as Record<string, unknown>).code === "23505";
          if (isUnique) {
            toast.error("Categoria já existe");
          } else {
            throw error;
          }
          return;
        }
        toast.success("Categoria atualizada com sucesso!");
      } else {
        const { error } = await supabase
          .from("categorias")
          .insert({
            nome: normalizedName,
            tipo: formData.tipo,
            cor: formData.cor,
            icone: formData.icone,
          });

        if (error) {
          const e = error as unknown;
          const isUnique = typeof e === "object" && e !== null && "code" in (e as Record<string, unknown>) && (e as Record<string, unknown>).code === "23505";
          if (isUnique) {
            toast.error("Categoria já existe");
          } else {
            throw error;
          }
          return;
        }
        toast.success("Categoria criada com sucesso!");
      }

      // Reset form
      setFormData({
        nome: "",
        tipo: "ambos",
        cor: "#3B82F6",
        icone: "default",
      });
      setEditingCategory(null);
      setModalOpen(false);

      fetchCategories();
      onCategoryUpdate?.();
    } catch (error) {
      console.error("Erro ao salvar categoria:", error);
      toast.error("Erro ao salvar categoria");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      nome: category.nome,
      tipo: category.tipo,
      cor: category.cor,
      icone: category.icone || "default",
    });
    setModalOpen(true);
  };

  const handleDelete = async (categoryId: string) => {
    try {
      const { error } = await supabase
        .from("categorias")
        .delete()
        .eq("id", categoryId);

      if (error) throw error;

      toast.success("Categoria excluída com sucesso!");

      fetchCategories();
      onCategoryUpdate?.();
    } catch (error) {
      console.error("Erro ao excluir categoria:", error);
      toast.error("Erro ao excluir categoria");
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCategory(null);
    setFormData({
      nome: "",
      tipo: "ambos",
      cor: "#3B82F6",
      icone: "default",
    });
  };

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case "entrada": return "Receita";
      case "saida": return "Despesa";
      case "ambos": return "Ambos";
      default: return tipo;
    }
  };

  const getTipoVariant = (tipo: string): { variant: "outline", className: string } => {
    switch (tipo) {
      case "entrada":
        return {
          variant: "outline",
          className: "border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-400 hover:bg-green-500/20"
        };
      case "saida":
        return {
          variant: "outline",
          className: "border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-400 hover:bg-red-500/20"
        };
      case "ambos":
        return {
          variant: "outline",
          className: "border-blue-500/20 bg-blue-500/10 text-blue-700 dark:text-blue-400 hover:bg-blue-500/20"
        };
      default:
        return {
          variant: "outline",
          className: ""
        };
    }
  };

  const handleAutoAssignIcons = async () => {
    setLoading(true);
    let updatedCount = 0;

    try {
      const updates = categories.map(async (category) => {
        // Se já tem um ícone específico (diferente de default ou null), pula
        if (category.icone && category.icone !== "default") return null;

        const normalized = normalizeName(category.nome);
        let matchedIcon = "default";

        // Tenta encontrar um ícone baseado nas palavras-chave
        for (const [icon, keywords] of Object.entries(ICON_KEYWORDS)) {
          if (keywords.some(k => normalized.includes(k))) {
            matchedIcon = icon;
            break;
          }
        }

        if (matchedIcon !== "default" && matchedIcon !== category.icone) {
          const { error } = await supabase
            .from("categorias")
            .update({ icone: matchedIcon })
            .eq("id", category.id);

          if (error) throw error;
          return 1;
        }
        return null;
      });

      const results = await Promise.all(updates);
      updatedCount = results.filter(r => r !== null).length;

      if (updatedCount > 0) {
        toast.success(`${updatedCount} categorias atualizadas com novos ícones!`);
        fetchCategories();
        onCategoryUpdate?.();
      } else {
        toast.info("Nenhuma categoria precisou ser atualizada.");
      }
    } catch (error) {
      console.error("Erro ao atualizar ícones:", error);
      toast.error("Erro ao atualizar ícones automaticamente");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <Dialog open={modalOpen} onOpenChange={(open) => !open && handleCloseModal()}>
            <DialogTrigger asChild>
              <Button onClick={() => setModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Categoria
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>
                  {editingCategory ? "Editar Categoria" : "Nova Categoria"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="nome">Nome</Label>
                  <Input
                    id="nome"
                    placeholder="Digite o nome da categoria"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    readOnly
                    onClick={(e) => {
                      e.currentTarget.readOnly = false;
                      e.currentTarget.focus();
                    }}
                    onBlur={(e) => {
                      e.currentTarget.readOnly = true;
                    }}
                  />
                </div>

                <div>
                  <Label htmlFor="tipo">Tipo</Label>
                  <Select value={formData.tipo} onValueChange={(value: "entrada" | "saida" | "ambos") => setFormData({ ...formData, tipo: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ambos">Ambos (Receita e Despesa)</SelectItem>
                      <SelectItem value="entrada">Apenas Receita</SelectItem>
                      <SelectItem value="saida">Apenas Despesa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="cor">Cor</Label>
                  <Select value={formData.cor} onValueChange={(value) => setFormData({ ...formData, cor: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a cor" />
                    </SelectTrigger>
                    <SelectContent>
                      {colors.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded-full border border-border"
                              style={{ backgroundColor: color.value }}
                            />
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="icone">Ícone</Label>
                  <Select value={formData.icone} onValueChange={(value) => setFormData({ ...formData, icone: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o ícone" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {AVAILABLE_ICONS.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          <div className="flex items-center gap-2">
                            <item.icon className="h-4 w-4" />
                            {item.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={loading} className="flex-1">
                    {loading ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            onClick={handleAutoAssignIcons}
            disabled={loading || categories.length === 0}
            title="Detectar ícones automaticamente baseados no nome"
          >
            <Wand2 className="h-4 w-4 mr-2" />
            Auto Ícones
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="relative">
            <Input
              placeholder="Buscar categorias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-accent/20"
            />
          </div>
          <div className="space-y-3">
            {categories.filter(c =>
              c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
              getTipoLabel(c.tipo).toLowerCase().includes(searchTerm.toLowerCase())
            ).length === 0 ? (
              <EmptyState
                icon={Tag}
                title={searchTerm ? "Nenhuma categoria encontrada" : "Sem categorias"}
                description={searchTerm
                  ? `Não encontramos resultados para "${searchTerm}". Tente outro termo.`
                  : "Você ainda não possui categorias cadastradas. Comece criando uma agora!"
                }
                actionLabel={searchTerm ? "Limpar busca" : "Nova Categoria"}
                onAction={() => searchTerm ? setSearchTerm("") : setModalOpen(true)}
              />
            ) : (
              categories
                .filter(c =>
                  c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  getTipoLabel(c.tipo).toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((category) => (
                  <div
                    key={category.id}
                    className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full border border-border flex items-center justify-center bg-accent/30"
                      >
                        {(() => {
                          const IconComponent = getCategoryIcon(category.icone);
                          return <IconComponent className="h-4 w-4" style={{ color: category.cor }} />;
                        })()}
                      </div>
                      <div>
                        <span className="font-medium">{category.nome}</span>
                        <div className="mt-1">
                            <Badge variant={getTipoVariant(category.tipo).variant} className={`text-xs ${getTipoVariant(category.tipo).className}`}>
                              {getTipoLabel(category.tipo)}
                            </Badge>
                          </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(category)}
                        aria-label="Editar categoria"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="Excluir categoria">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir categoria</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir a categoria "{category.nome}"?
                              Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(category.id)}>
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
